import {
  AbilityTuple,
  createMongoAbility,
  mongoQueryMatcher,
  MongoAbility,
  MongoQuery,
  subject as conditionalSubject,
  SubjectRawRule,
  SubjectType,
} from '@casl/ability';
import type { UserInterface } from 'context/auth.context';

export enum PermissionAction {
  Access = 'access',
  Complete = 'complete',
  Create = 'create',
  Delete = 'delete',
  Manage = 'manage',
  Read = 'read',
  Update = 'update',
}

export type TenantScopeCondition = { tenantId: number };
export type CampaignScopeCondition = { campaignId: number };
export type ActivityScopeCondition = { activityId: number };
export type StoreScopeCondition = { storeId: number };
export type CampaignStoreScopeCondition = { campaignStoreId: number };
export type ActivityAssignmentScopeCondition = { activityAssignmentId: number };
export type StepResponseScopeCondition = { stepResponseId: number };

export type Condition =
  | TenantScopeCondition
  | CampaignScopeCondition
  | ActivityScopeCondition
  | StoreScopeCondition
  | CampaignStoreScopeCondition
  | ActivityAssignmentScopeCondition
  | StepResponseScopeCondition
  | object;

export type AppRuleInterface = SubjectRawRule<
  string,
  SubjectType,
  MongoQuery<Record<string | number | symbol, unknown>>
>;

export type AppAbilityInterface = MongoAbility<
  AbilityTuple,
  MongoQuery<Record<string | number | symbol, unknown>>
>;

export type CaslPermission = {
  action: string;
  subject: string;
  conditions?: Condition | Condition[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isStringOrStrings = (value: unknown): value is string | string[] =>
  typeof value === 'string' ||
  (Array.isArray(value) && value.every((item) => typeof item === 'string'));

function readRule(value: unknown): AppRuleInterface | undefined {
  if (!isRecord(value) || !isStringOrStrings(value.action) || !isStringOrStrings(value.subject))
    return;
  if (value.inverted !== undefined && typeof value.inverted !== 'boolean') return;
  if (value.reason !== undefined && typeof value.reason !== 'string') return;
  if (
    value.fields !== undefined &&
    (!isStringOrStrings(value.fields) || (Array.isArray(value.fields) && !value.fields.length))
  )
    return;
  if (value.conditions !== undefined && !isRecord(value.conditions)) return;

  // CASL compiles conditions lazily. Validate them now so malformed operators
  // cannot throw later while rendering a permission-dependent route.
  if (value.conditions) mongoQueryMatcher(value.conditions);
  return {
    action: value.action,
    subject: value.subject,
    ...(value.inverted !== undefined && { inverted: value.inverted }),
    ...(value.reason !== undefined && { reason: value.reason }),
    ...(value.fields !== undefined && { fields: value.fields }),
    ...(value.conditions !== undefined && { conditions: value.conditions }),
  };
}

export const newAbility = (): AppAbilityInterface => createMongoAbility<AppAbilityInterface>();

export const generateAbility = (
  user: Pick<UserInterface, 'caslAbilities'>
): AppAbilityInterface => {
  try {
    const value: unknown = user.caslAbilities ? JSON.parse(user.caslAbilities) : undefined;
    if (!isRecord(value) || !Array.isArray(value.rules)) return newAbility();
    const rules: AppRuleInterface[] = [];
    for (const candidate of value.rules) {
      const rule = readRule(candidate);
      // Reject the whole set: skipping a malformed denial could preserve an
      // earlier grant and accidentally widen the user's permissions.
      if (!rule) return newAbility();
      rules.push(rule);
    }
    return createMongoAbility<AppAbilityInterface>(rules);
  } catch {
    return newAbility();
  }
};

export const hasPermissions = (
  ability: AppAbilityInterface,
  action: string,
  subject: string,
  conditions?: object
) => {
  // Check for SuperTenant permission
  if (ability.can('access', 'SuperTenant')) {
    return true;
  }

  if (conditions) {
    return ability.can(action, conditionalSubject(subject, { ...conditions }));
  }
  return ability.can(action, subject);
};
